import Link from "next/link";
import React from "react";
import SiteShell from "@/components/SiteShell";
import FoundingMemberBanner from "@/components/FoundingMemberBanner";

const TOOLS = [
  {
    href: "/tools/funnel-generator",
    icon: "🗺",
    name: "Funnel Map Generator",
    blurb:
      "Describe your business in a sentence — get a complete, personalized funnel map: pages, email sequence, waits and branches. Claim it into a free workspace and it runs for real.",
    cta: "Generate a funnel",
  },
  {
    href: "/tools/email-link-checker",
    icon: "🔗",
    name: "Email Link Checker",
    blurb:
      "Paste your email HTML and get an instant health report on every link — status codes, redirect chains, timeouts — before your subscribers find the 404 for you.",
    cta: "Check your links",
  },
  {
    href: "/tools/cost-calculator",
    icon: "🧮",
    name: "Automation Cost Calculator",
    blurb:
      "Slide to your contacts, email volume and client count — see what your funnel costs per month on ActiveCampaign, GoHighLevel, Make and FrazzleCRM. Assumptions shown.",
    cta: "Run the numbers",
  },
];

/** Free-tools hub (www.frazzlecrm.com/tools) — indexable. */
export default function ToolsIndex() {
  return (
    <SiteShell
      title="Free marketing tools — FrazzleCRM"
      description="Free marketing tools from FrazzleCRM: an AI funnel map generator and an email link checker. No signup needed to use them."
    >
      <FoundingMemberBanner />

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-4xl px-6 pb-10 pt-16 text-center">
          <h1 className="text-[36px] font-extrabold leading-[1.12] tracking-tight text-slate-900 sm:text-[44px]">
            Free tools.{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              No signup.
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-[15.5px] leading-relaxed text-slate-600">
            Useful on their own — and a taste of how FrazzleCRM thinks:
            visual, honest, and allergic to busywork.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-20">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {TOOLS.map((t) => (
            <Link
              key={t.href}
              href={t.href}
              className="group flex flex-col rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="text-[30px]">{t.icon}</div>
              <h2 className="mt-3 text-[18px] font-bold text-slate-900 group-hover:text-indigo-700">
                {t.name}
              </h2>
              <p className="mt-2 flex-1 text-[13.5px] leading-relaxed text-slate-600">
                {t.blurb}
              </p>
              <span className="mt-4 text-[13px] font-semibold text-indigo-600 group-hover:translate-x-0.5">
                {t.cta} →
              </span>
            </Link>
          ))}
        </div>
        <p className="mt-10 text-center text-[13px] text-slate-500">
          Want the full picture? Browse the{" "}
          <Link href="/templates" className="font-medium text-indigo-600 hover:text-indigo-500">
            funnel template gallery
          </Link>{" "}
          or see how{" "}
          <Link href="/ai" className="font-medium text-indigo-600 hover:text-indigo-500">
            your AI can drive FrazzleCRM
          </Link>
          .
        </p>
        <p className="mt-3 text-center text-[13px] text-slate-500">
          Not sure what a stop tag or a send window is? The{" "}
          <Link href="/learn" className="font-medium text-indigo-600 hover:text-indigo-500">
            automation glossary
          </Link>{" "}
          defines every moving part in plain English.
        </p>
        <p className="mt-3 text-center text-[13px] text-slate-500">
          Built something worth showing?{" "}
          <Link href="/embed" className="font-medium text-indigo-600 hover:text-indigo-500">
            Embed a live funnel map
          </Link>{" "}
          in your own page — free, no script tag.
        </p>
      </section>
    </SiteShell>
  );
}
